import { useState, useEffect } from 'react';

const MoodTracker = ({ darkMode = false }) => {
  const [selectedMood, setSelectedMood] = useState(null);
  const [moodHistory, setMoodHistory] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const moods = [
    { emoji: '😄', label: 'Very Happy', value: 5 },
    { emoji: '😊', label: 'Happy', value: 4 },
    { emoji: '😐', label: 'Neutral', value: 3 },
    { emoji: '😔', label: 'Sad', value: 2 },
    { emoji: '😢', label: 'Very Sad', value: 1 }
  ];

  // Load mood history from localStorage
  useEffect(() => {
    const savedMoods = localStorage.getItem('moodHistory');
    if (savedMoods) {
      try {
        setMoodHistory(JSON.parse(savedMoods));
      } catch (error) {
        console.error('Error parsing mood history:', error);
      }
    }
  }, []);

  const handleMoodSelect = (mood) => {
    setSelectedMood(mood);
    setIsSubmitted(false);
  };

  const handleSubmit = () => {
    if (!selectedMood) return;

    const newEntry = {
      ...selectedMood,
      timestamp: new Date().toISOString()
    };

    const updatedHistory = [newEntry, ...moodHistory].slice(0, 5);
    setMoodHistory(updatedHistory);
    localStorage.setItem('moodHistory', JSON.stringify(updatedHistory));
    setIsSubmitted(true);
    setSelectedMood(null);
  };

  return (
    <div className="px-6">
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-lg max-w-3xl mx-auto p-6 sm:p-10 space-y-10">

        {/* Heading */}
        <div className="text-center">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-white tracking-tight mb-2">
            How are you feeling today?
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Take a moment to check in with yourself.
          </p>
        </div>

        {/* Mood Buttons */}
        <div className="flex justify-center flex-wrap gap-4">
          {moods.map((mood, index) => (
            <button
              key={mood.label}
              onClick={() => handleMoodSelect(mood)}
              className={`w-16 h-16 sm:w-20 sm:h-20 text-3xl rounded-full transition-all duration-300 transform
                flex items-center justify-center shadow-md hover:shadow-xl active:scale-95 focus:outline-none
                animate-fade-in-scale
                ${
                  selectedMood?.label === mood.label
                    ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white scale-110 ring-4 ring-purple-300 shadow-wellness'
                    : 'bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 text-gray-800 dark:text-gray-200 hover:from-purple-100 hover:to-pink-100 dark:hover:from-purple-800/30 dark:hover:to-pink-800/30 hover:scale-105'
                }`}
              style={{
                animationDelay: `${index * 0.1}s` // Staggered animation
              }}
              title={mood.label}
            >
              <span className="transition-transform duration-200 hover:scale-110">
                {mood.emoji}
              </span>
            </button>
          ))}
        </div>

        {/* Submit Button */}
        <div className="text-center">
          <button
            onClick={handleSubmit}
            disabled={!selectedMood}
            className={`px-8 py-3 rounded-xl text-sm font-semibold transition-all duration-300 shadow-md transform
              ${
                selectedMood
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 hover:shadow-xl hover:scale-105 active:scale-95'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
          >
            {selectedMood ? `Save ${selectedMood.emoji} Mood` : 'Select a mood first'}
          </button>
        </div>

        {/* Confirmation Message */}
        {isSubmitted && (
          <div className="text-center animate-fade-in-scale">
            <div className="text-green-600 dark:text-green-400 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 px-6 py-4 rounded-2xl shadow-soft inline-flex items-center space-x-2 animate-bounce-gentle">
              <span className="text-2xl">✅</span>
              <span className="font-medium">Mood saved successfully!</span>
            </div>
          </div>
        )}

        {/* Mood History */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 text-center">
            Recent Moods
          </h3>
          {moodHistory.length === 0 ? (
            <p className="text-gray-500 text-center text-sm">
              No moods recorded yet. Start by selecting how you feel.
            </p>
          ) : (
            <ul className="space-y-3">
              {moodHistory.map((entry, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between bg-gray-100 dark:bg-gray-700 px-4 py-3 rounded-xl shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{entry.emoji}</span>
                    <span className="text-gray-800 dark:text-white">{entry.label}</span>
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-300">
                    {new Date(entry.timestamp).toLocaleDateString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default MoodTracker;